// ── Colorblind Accessibility Modes ──
// Provides color remapping for deuteranopia, protanopia, and tritanopia.
// Injects CSS custom properties (--enemy-color, --friendly-color, etc.) via body class.

// ─── Mode Enum ───────────────────────────────────────────────────────────────

export enum ColorblindMode {
  None = 'none',
  Deuteranopia = 'deuteranopia',   // red-green (most common)
  Protanopia = 'protanopia',       // red-green (alt)
  Tritanopia = 'tritanopia',       // blue-yellow
}

// ─── CSS Class Names ─────────────────────────────────────────────────────────

export const COLORBLIND_CSS_CLASSES: Partial<Record<ColorblindMode, string>> = {
  [ColorblindMode.Deuteranopia]: 'colorblind-deuteranopia',
  [ColorblindMode.Protanopia]: 'colorblind-protanopia',
  [ColorblindMode.Tritanopia]: 'colorblind-tritanopia',
};

// ─── Color Remap Tables ──────────────────────────────────────────────────────
// Each table maps a canonical hex color (lowercased) to its colorblind-safe replacement.

const DEUTERANOPIA_MAP: Record<string, string> = {
  '#ff0000': '#ff8800',   // enemy red → orange
  '#cc0000': '#cc7700',   // danger dark red → dark orange
  '#0000ff': '#00ccff',   // friendly blue → cyan
  '#00ff00': '#00ffff',   // health green → cyan
};

const PROTANOPIA_MAP: Record<string, string> = {
  '#ff0000': '#ff00ff',   // enemy red → magenta
  '#cc0000': '#cc00cc',   // danger dark red → dark magenta
  '#0000ff': '#0000ff',   // friendly blue → stays blue
  '#00ff00': '#ffffff',   // health green → white
};

const TRITANOPIA_MAP: Record<string, string> = {
  '#ff0000': '#ff0000',   // enemy red → stays red
  '#cc0000': '#cc0000',   // danger dark red → stays
  '#0000ff': '#ffffff',   // friendly blue → white
  '#00ff00': '#00ffcc',   // health green → cyan-green
};

const MODE_MAPS: Record<ColorblindMode, Record<string, string> | null> = {
  [ColorblindMode.None]: null,
  [ColorblindMode.Deuteranopia]: DEUTERANOPIA_MAP,
  [ColorblindMode.Protanopia]: PROTANOPIA_MAP,
  [ColorblindMode.Tritanopia]: TRITANOPIA_MAP,
};

// ─── Color Remapping ─────────────────────────────────────────────────────────

/**
 * Remap a hex color string for the given colorblind mode.
 * Returns the original color if no remapping exists for that mode/color.
 */
export function remapColor(hex: string, mode: ColorblindMode): string {
  if (mode === ColorblindMode.None) return hex;
  const map = MODE_MAPS[mode];
  if (!map) return hex;
  const key = hex.toLowerCase();
  return map[key] ?? hex;
}

// ─── Team Colors ─────────────────────────────────────────────────────────────

const DEFAULT_FRIENDLY = '#0000ff';
const DEFAULT_ENEMY = '#ff0000';

/**
 * Get team indicator colors for the given colorblind mode.
 */
export function getTeamColors(mode: ColorblindMode): { friendly: string; enemy: string } {
  return {
    friendly: remapColor(DEFAULT_FRIENDLY, mode),
    enemy: remapColor(DEFAULT_ENEMY, mode),
  };
}

// ─── CSS Custom Property Values ──────────────────────────────────────────────

interface ColorblindCSSVars {
  enemyColor: string;
  friendlyColor: string;
  dangerColor: string;
  healthColor: string;
}

function getCSSVars(mode: ColorblindMode): ColorblindCSSVars {
  return {
    enemyColor: remapColor('#ff0000', mode),
    friendlyColor: remapColor('#0000ff', mode),
    dangerColor: remapColor('#cc0000', mode),
    healthColor: remapColor('#00ff00', mode),
  };
}

const STYLE_ELEMENT_ID = 'colorblind-styles';
const ALL_CLASSES = Object.values(COLORBLIND_CSS_CLASSES) as string[];

// ─── CSS Injection ───────────────────────────────────────────────────────────

/**
 * Apply colorblind CSS class to document.body and inject a <style> element
 * with CSS custom properties (--enemy-color, --friendly-color, etc.).
 *
 * Removes any previous colorblind class first.
 */
export function applyColorblindCSS(mode: ColorblindMode): void {
  // Remove any existing colorblind classes
  removeColorblindCSS();

  if (mode === ColorblindMode.None) return;

  const className = COLORBLIND_CSS_CLASSES[mode];
  if (!className) return;

  // Add body class
  document.body.classList.add(className);

  // Inject <style> with custom properties
  const vars = getCSSVars(mode);
  const css = `
.${className} {
  --enemy-color: ${vars.enemyColor};
  --friendly-color: ${vars.friendlyColor};
  --danger-color: ${vars.dangerColor};
  --health-color: ${vars.healthColor};
}`;

  const styleEl = document.createElement('style');
  styleEl.id = STYLE_ELEMENT_ID;
  styleEl.textContent = css;
  document.head.appendChild(styleEl);
}

/**
 * Remove all colorblind CSS classes and the injected <style> element.
 */
export function removeColorblindCSS(): void {
  // Remove all colorblind classes from body
  for (const cls of ALL_CLASSES) {
    document.body.classList.remove(cls);
  }

  // Remove injected style element
  const existing = document.getElementById(STYLE_ELEMENT_ID);
  if (existing) existing.remove();
}
